import { Storage } from 'aws-amplify';
import dayjs from '../../../dayjs';
import { reportsModelData, routesModelExpiryService } from '../../../services/ModelService';
import { getStops, routeNumberSort } from '../../../services/routeService';
import { fetchMasterStopsInBounds } from '../../../services/routeService';
import { format } from 'date-fns';
import Papa from 'papaparse';
import { ulid } from 'ulid';
import util from 'util';

// OTR Performance threshold values - ensure these match OTRDashboard
export const OTR_THRESHOLDS = {
  early: -2*60,   // 2 minutes early is considered early
  late: 6*60,  // 6 minutes late is considered late
  veryLate: 12*60, // 12 minutes late is considered very late
};

// Local storage key for OTR data
const OTR_DATA_KEY = 'busable_otr_data';
const OTR_DATA_META_KEY = 'busable_otr_data_meta';
const OTR_STOP_DATA_KEY = 'busable_otr_stop_data';

const getReportUrl = async (report) => {
    let key = report.fileKey
    if(report.fileKey.startsWith('private/')) {
      key = report.fileKey.replace('private/', '');
    }
    return await Storage.get(key, {
      level: 'public',
      customPrefix: {
        public: 'private/'
      },
      completeCallback: (event) => {
          console.log(`Successfully uploaded ${key}`);
      },
      progressCallback: (progress) => {
          console.log(`Uploaded: ${progress.loaded}/${progress.total}`);
      },
      errorCallback: (err) => {
          console.error('Unexpected error while uploading', err);
      },
      expires: 3600 // 1 hour expiration
    });
  };

/**
 * Service for managing OTR data
 */
class OTRDataService {

  thresholds = OTR_THRESHOLDS;

  constructor(kpiThresholds) {
    this.thresholds = kpiThresholds || OTR_THRESHOLDS;
  } 

  processFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const content = e.target.result;
        
        // Store first few lines for debugging
        const firstLines = content.split('\n').slice(0, 5).join('\n');
        
        // First try to detect delimiter by checking first few lines
        let delimiter = this.detectDelimiter(content);
        
        // Check for UTF-8 BOM
        const hasBOM = content.charCodeAt(0) === 0xFEFF;
        const cleanContent = hasBOM ? content.slice(1) : content;
        
        // Try different parsing strategies
        this.tryParseFile(cleanContent, delimiter, file, resolve, reject);
      };
      
      reader.onerror = (error) => {
        console.error('File read error:', error);
        reject(new Error(`Failed to read file ${file.name}: ${error.message || 'Unknown error'}`));
      };
      
      // Try reading as text first
      reader.readAsText(file);
    });
  };
  
  // Try different parsing strategies
  tryParseFile = (content, initialDelimiter, file, resolve, reject) => {
    // First attempt with detected delimiter
    this.parseWithDelimiter(content, initialDelimiter, file, (success, result, error) => {
      if (success) {
        resolve(result);
      } else {
        // Second attempt with alternate delimiter
        const alternateDelimiter = initialDelimiter === ';' ? ',' : ';';
        this.parseWithDelimiter(content, alternateDelimiter, file, (success2, result2, error2) => {
          if (success2) {
            resolve(result2);
          } else {
            // Third attempt with tab delimiter
            this.parseWithDelimiter(content, '\t', file, (success3, result3, error3) => {
              if (success3) {
                resolve(result3);
              } else {
                // Last attempt with auto-detection
                Papa.parse(content, {
                  header: true,
                  delimiter: 'auto',  
                  dynamicTyping: true,
                  skipEmptyLines: 'greedy',
                  transformHeader: (header) => header.trim().replace(/^\uFEFF/, ''),
                  complete: (results) => {
                    if (results.data && results.data.length > 0) {
                      this.processResults(results, file, resolve, reject);
                    } else {
                      reject(new Error(`Could not parse file ${file.name} with any known delimiter`));
                    }
                  },
                  error: (error) => {
                    reject(new Error(`Auto-detection parse error: ${error.message}`));
                  }
                });
              }
            });
          }
        });
      }
    });
  };
  
  // Parse with specific delimiter
  parseWithDelimiter = (content, delimiter, file, callback) => {
    Papa.parse(content, {
      header: true,
      delimiter: delimiter,
      dynamicTyping: true,
      skipEmptyLines: 'greedy',
      transformHeader: (header) => header.trim().replace(/^\uFEFF/, ''),
      complete: (results) => {
        if (results.data && results.data.length > 0) {
          this.processResults(results, file, 
            (result) => callback(true, result), 
            (error) => callback(false, null, error)
          );
        } else {
          callback(false, null, new Error(`No data rows found with delimiter: ${delimiter}`));
        }
      },
      error: (error) => {
        callback(false, null, error);
      }
    });
  };
  
  // Helper function to detect delimiter from file content
  detectDelimiter = (content) => {
    // Take first few lines to detect
    const lines = content.split('\n').slice(0, 5).join('\n');
    
    // Count occurrences of potential delimiters
    const semicolons = (lines.match(/;/g) || []).length;
    const commas = (lines.match(/,/g) || []).length;
    const tabs = (lines.match(/\t/g) || []).length;
    
    // Check if we have more semicolons or commas in header row
    const firstLine = content.split('\n')[0] || '';
    const headerSemicolons = (firstLine.match(/;/g) || []).length;
    const headerCommas = (firstLine.match(/,/g) || []).length;
    const headerTabs = (firstLine.match(/\t/g) || []).length;
    
    // Prefer header row delimiters first
    if (headerSemicolons > 0) return ';';
    if (headerCommas > 0) return ',';
    if (headerTabs > 0) return '\t';
    
    // If no clear delimiter in header, use most common
    if (semicolons > commas && semicolons > tabs) return ';';
    if (commas > semicolons && commas > tabs) return ',';
    if (tabs > semicolons && tabs > commas) return '\t';
    
    // Default to semicolon if we can't detect
    return ';';
  };
  
  // Process the results of Papa.parse
  processResults = (results, file, resolve, reject) => {
    try {
      const { data, errors, meta } = results;
      
      // Check if the file has the expected structure
      if (!data || data.length === 0) {
        reject(new Error('No data rows found in file after parsing'));
        return;
      }
      
      const firstRow = data[0];
      
      const requiredFields = ['DATE_ID', 'ROUTE', 'TRIP_ID', 'SCHEDULED_ARRIVAL_TIME', 'IS_TIMING_POINT', 'DRIVER_ID', 'DEPOT', 'TSN'];
      
      // Optional fields that should be used if available
      const optionalFields = [
        'SCHEDULED_DEPARTURE_TIME', 'ACTUAL_DEPARTURE_TIME', 'DEPARTURE_TIME_VARIANCE', 
        'ACTUAL_ARRIVAL_TIME',         'TSN_FIRST_LAST_IND',
        'OPERATOR_ID', 'CONTRACT_CODE', 'CONTRACT', 'TIME_RECORD_SEQUENCE', 'ARRIVAL_TIME_VARIANCE', 'ROUTE_TYPE_NAME', 'DIRECTION', 'VEHICLE_SHIFT'
      ];
      
      // Allow for different case variations in field names
      const normalizedFields = {};
      Object.keys(firstRow).forEach(key => {
        // Normalize keys by removing spaces, underscores, and ignoring case
        const normalizedKey = key.toUpperCase()
          .replace(/\s+/g, '_')
          .replace(/[^A-Z0-9_]/g, '');
        
        normalizedFields[normalizedKey] = key;
        
        // Also try with common variations
        if (normalizedKey === 'DATEID') normalizedFields['DATE_ID'] = key;
        if (normalizedKey === 'TRIPID') normalizedFields['TRIP_ID'] = key;
        if (normalizedKey === 'SCHEDULEDARRIVALTIME') normalizedFields['SCHEDULED_ARRIVAL_TIME'] = key;
        if (normalizedKey === 'ISTIMINGPOINT') normalizedFields['IS_TIMING_POINT'] = key;
        if (normalizedKey === 'DRIVERID') normalizedFields['DRIVER_ID'] = key;
        if (normalizedKey === 'TIMERECORDSEQUENCE') normalizedFields['TIME_RECORD_SEQUENCE'] = key;
        if (normalizedKey === 'TIMEREC' || normalizedKey === 'TIME_REC') normalizedFields['TIME_RECORD_SEQUENCE'] = key;
        
        // TSN field variations
        if (normalizedKey === 'TSN' || normalizedKey === 'TRANSITSTOPNUMBER') normalizedFields['TSN'] = key;
        if (normalizedKey === 'MASTERSTOPNO' || normalizedKey === 'MASTER_STOP_NO') normalizedFields['TSN'] = key;
        if (normalizedKey === 'STOPID' || normalizedKey === 'STOP_ID') normalizedFields['TSN'] = key;
        if (normalizedKey === 'STOPCODE' || normalizedKey === 'STOP_CODE') normalizedFields['TSN'] = key;
        
        // Add optional field variations
        if (normalizedKey === 'SCHEDULEDDEPARTURETIME') normalizedFields['SCHEDULED_DEPARTURE_TIME'] = key;
        if (normalizedKey === 'ACTUALARRIVALTIME') normalizedFields['ACTUAL_ARRIVAL_TIME'] = key;
        if (normalizedKey === 'ACTUALDEPARTURETIME') normalizedFields['ACTUAL_DEPARTURE_TIME'] = key;
        if (normalizedKey === 'DEPARTURETIMEVARIANCE') normalizedFields['DEPARTURE_TIME_VARIANCE'] = key;
        if (normalizedKey === 'ARRIVALTIMEVARIANCE') normalizedFields['ARRIVAL_TIME_VARIANCE'] = key;
        if (normalizedKey === 'TSNFIRSTLASTIND') normalizedFields['TSN_FIRST_LAST_IND'] = key;
        if (normalizedKey === 'OPERATORID') normalizedFields['OPERATOR_ID'] = key;
        if (normalizedKey === 'CONTRACTCODE') normalizedFields['CONTRACT_CODE'] = key;
        if (normalizedKey === 'CONTRACTID') normalizedFields['CONTRACT'] = key;
        if (normalizedKey === 'ROUTETYPENAME') normalizedFields['ROUTE_TYPE_NAME'] = key;
        if (normalizedKey === 'DIRECTION') normalizedFields['DIRECTION'] = key;
        if (normalizedKey === 'VEHICLESHIFT') normalizedFields['VEHICLE_SHIFT'] = key;
      });
      
      const missingFields = requiredFields.filter(field => {
        return !normalizedFields[field];
      });
      
      if (missingFields.length > 0) {
        const error = new Error(`Missing required fields: ${missingFields.join(', ')}, ${util.inspect(firstRow)}`);
        error.availableFields = Object.keys(firstRow);
        reject(error);
        return;
      }
      
      // Map normalized field names to actual field names from file
      const fieldMap = {};
      requiredFields.forEach(field => {
        fieldMap[field] = normalizedFields[field];
      });
      
      // Map optional fields if available
      optionalFields.forEach(field => {
        if (normalizedFields[field]) {
          fieldMap[field] = normalizedFields[field];
        }
      });
      
      // Special handling for CONTRACT field - use CONTRACT_CODE if CONTRACT is not available
      if (normalizedFields['CONTRACT_CODE'] && !normalizedFields['CONTRACT']) {
        fieldMap['CONTRACT'] = normalizedFields['CONTRACT_CODE'];
      }
      
      // Process the data for OTR analysis
      const processedData = data
        .filter(row => {
          // Filter out rows with no DATE_ID or TRIP_ID
          const hasDateId = row[fieldMap['DATE_ID']] !== null && row[fieldMap['DATE_ID']] !== undefined;
          const hasTripId = row[fieldMap['TRIP_ID']] !== null && row[fieldMap['TRIP_ID']] !== undefined;
          return hasDateId && hasTripId;
        })
        .map(row => {
          // Create a normalized row with expected field names
          const normalizedRow = {};
          
          // Add required fields
          requiredFields.forEach(field => {
            normalizedRow[field] = row[fieldMap[field]];
          });
          
          // Add optional fields if available
          optionalFields.forEach(field => {
            if (fieldMap[field]) {
              normalizedRow[field] = row[fieldMap[field]];
            }
          });
          
          // If DEPARTURE_TIME_VARIANCE is not available but we have departure times, calculate it
          if (!Number.isFinite(normalizedRow.DEPARTURE_TIME_VARIANCE) && 
              normalizedRow.SCHEDULED_DEPARTURE_TIME && 
              normalizedRow.ACTUAL_DEPARTURE_TIME) {
            try {
              // Calculate variance - this is a simplification and might need to be adjusted
              // based on the actual time format in your data
              const scheduledTime = this.parseTimeString(normalizedRow.SCHEDULED_DEPARTURE_TIME);
              const actualTime = this.parseTimeString(normalizedRow.ACTUAL_DEPARTURE_TIME);
              
              if (scheduledTime && actualTime) {
                // Calculate variance in seconds
                const variance = (actualTime - scheduledTime);
                normalizedRow.DEPARTURE_TIME_VARIANCE = variance;
              }
            } catch (err) {
              console.warn(`Could not calculate departure variance: ${err.message}`);
            }
          }
          
          // Enhance data with derived fields useful for analysis
          return {
            sourceData: row,
            ...normalizedRow,
            DATE_FORMATTED: normalizedRow.DATE_ID ? dayjs(String(normalizedRow.DATE_ID)).format('YYYY-MM-DD') : null,
            ON_TIME: Number.isFinite(normalizedRow.DEPARTURE_TIME_VARIANCE)
              ? this.calculateOnTimeStatus(normalizedRow.DEPARTURE_TIME_VARIANCE)
              : this.calculateOnTimeStatus(normalizedRow.ARRIVAL_TIME_VARIANCE),
            STOP_TYPE: this.getStopType(normalizedRow.TSN_FIRST_LAST_IND)
          };
        });
      
      // If we filtered out all rows, reject
      if (processedData.length === 0) {
        reject(new Error('All rows were filtered out due to missing required data'));
        return;
      }
      
      resolve({
        fileName: file.name,
        totalRows: data.length,
        processedRows: processedData.length,
        filteredRows: data.length - processedData.length,
        errorRows: errors.length,
        data: processedData,
        errors: errors,
        meta: meta
      });
    } catch (error) {
      console.error("Error processing CSV results:", error);
      reject(new Error(`Error processing file ${file.name}: ${error.message}`));
    }
  };
  
  // Process the normalized data into a standard format
  processNormalizedData = (normalizedData) => {
    try {
      // Make sure route values are consistently stored as strings
      return normalizedData.map(record => {
        const processed = { ...record };
        
        // Convert route to string to ensure consistent handling
        if (processed.ROUTE !== undefined && processed.ROUTE !== null) {
          processed.ROUTE = String(processed.ROUTE);
        }
        
        // Ensure TSN is a string 
        if (processed.TSN !== undefined && processed.TSN !== null) {
          processed.TSN = String(processed.TSN);
        }
        
        return processed;
      });
    } catch (error) {
      console.error('Error processing normalized data:', error);
      return normalizedData;
    }
  };

  // Parse a time string in various formats
  parseTimeString = (timeStr) => {
    if (!timeStr) return null;
    
    try {
      // If it's already a number, just return it
      if (typeof timeStr === 'number' && !isNaN(timeStr)) {
        return timeStr;
      }
      
      // Convert to string if it's not one already
      const timeString = String(timeStr);
      
      // Handle date-time format (e.g., "2025-04-02 14:10:00")
      if (timeString.includes(' ')) {
        const parts = timeString.split(' ');
        if (parts.length >= 2) {
          const timePart = parts[1];
          if (timePart.includes(':')) {
            const [hours, minutes, seconds = 0] = timePart.split(':').map(Number);
            if (!isNaN(hours) && !isNaN(minutes)) {
              // Convert to minutes past midnight
              return hours * 3600 + minutes * 60 + seconds;
            }
          }
        }
      } 
      // Handle time format (e.g., "14:10:00")
      else if (timeString.includes(':')) {
        const [hours, minutes, seconds = 0] = timeString.split(':').map(Number);
        if (!isNaN(hours) && !isNaN(minutes)) {
          // Convert to minutes past midnight
          return hours * 3600 + minutes * 60 + seconds;
        }
      }
      
      console.warn(`Could not parse time string: ${timeString}`);
      return null;
    } catch (error) {
      console.warn(`Error parsing time string: ${timeStr}`, error);
      return null;
    }
  };

  calculateOnTimeStatus = (variance) => {
    if (variance === null || variance === undefined) return null;
    
    // Use threshold variables instead of hardcoded values
    if (variance >= this.thresholds.earlyThreshold && variance <= this.thresholds.lateThreshold) return 'ON_TIME';
    if (variance < this.thresholds.earlyThreshold) return 'EARLY';
    return 'LATE';
  };

  getStopType = (tsnIndicator) => {
    if (!tsnIndicator) return 'MID';
    
    switch (tsnIndicator) {
      case 'F': return 'FIRST';
      case 'M': return 'MID';
      case 'L': return 'LAST';
      default: return 'UNKNOWN';
    }
  };


  /**
   * Store OTR data in local storage
   * @param {Array} data - The OTR data records
   * @returns {Object} Metadata about the stored data
   */
  async storeData(data) {
    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error('Invalid data format for OTR data storage');
    }
    
    try {
      
      // Debug route data
      console.log("Analyzing route data before storage...");
      const routeTypes = data.reduce((acc, item) => {
        const type = typeof item.ROUTE;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      console.log("Route data types:", routeTypes);
      
      const uniqueRoutes = [...new Set(data.map(item => item.ROUTE))].filter(Boolean);
      console.log(`Found ${uniqueRoutes.length} unique routes`, uniqueRoutes.slice(0, 20));
      
      // Ensure all routes are strings for consistency
      if (routeTypes.number > 0) {
        console.log("Converting numeric routes to strings for consistency");
        data = data.map(item => ({
          ...item,
          ROUTE: item.ROUTE !== undefined && item.ROUTE !== null ? String(item.ROUTE) : item.ROUTE
        }));
      }
      
      // Calculate metadata
      const uniqueDates = [...new Set(data.map(item => item.DATE_ID))].sort();
      const uniqueRoutesSorted = [...new Set(data.map(item => item.ROUTE))].filter(Boolean).sort();
      const uniqueDrivers = [...new Set(data.map(item => item.DRIVER_ID).filter(Boolean))];
      const uniqueDepots = [...new Set(data.map(item => item.DEPOT).filter(Boolean))];
      const uniqueOperatorIds = [...new Set(data.map(item => item.OPERATOR_ID).filter(Boolean))];
      const uniqueContracts = [...new Set(data.map(item => item.CONTRACT).filter(Boolean))];
      const uniqueRouteTypes = [...new Set(data.map(item => item.ROUTE_TYPE_NAME).filter(Boolean))];
      const uniqueDirections = [...new Set(data.map(item => item.DIRECTION).filter(Boolean))];
      const totalRecords = data.length;
      
      // Create metadata object
      const metadata = {
        startDate: uniqueDates[0],
        endDate: uniqueDates[uniqueDates.length - 1],
        dateRange: {
          start: uniqueDates[0],
          end: uniqueDates[uniqueDates.length - 1]
        },
        routes: uniqueRoutesSorted,
        drivers: uniqueDrivers,
        depots: uniqueDepots,
        operatorIds: uniqueOperatorIds,
        contracts: uniqueContracts,
        routeTypes: uniqueRouteTypes,
        directions: uniqueDirections,
        totalRecords,
        lastUpdated: new Date().toISOString()
      };

      // routesModelExpiryService.db.otrs.put(metadata);

      
      // // If the dataset is extremely large, use a sampling approach
      // const MAX_TOTAL_STORAGE = 50 * 1024 * 1024; // Increase from 4MB to 50MB max total storage
      
      // // Estimate size of serialized data
      // const sampleSize = 100;
      // const sample = data.slice(0, sampleSize);
      // const sampleJson = JSON.stringify(sample);
      // const estimatedFullSize = (sampleJson.length / sampleSize) * totalRecords;
      
      // console.log(`Estimated data size: ${Math.round(estimatedFullSize / 1024)} KB`);
      
      // // If data is too large, use sampling strategy
      // if (estimatedFullSize > MAX_TOTAL_STORAGE) {
      //   console.log("Dataset large but attempting full storage first");
        
      //   try {
      //     // Try storing all data in chunks
      //     const MAX_CHUNK_SIZE = 256;
      //     const chunks = [];
          
      //     for (let i = 0; i < data.length; i += MAX_CHUNK_SIZE) {
      //       const chunk = data.slice(i, i + MAX_CHUNK_SIZE);
      //       chunks.push(chunk);
      //     }
          
      //     // Store chunks
      //     const storedChunks = [];
      //     for (let i = 0; i < chunks.length; i++) {
      //       try {
      //         localStorage.setItem(`${OTR_DATA_KEY}_chunk_${i}`, JSON.stringify(chunks[i]));
      //         storedChunks.push(i);
      //       } catch (e) {
      //         console.warn(`Storage quota reached after ${storedChunks.length} chunks`);
      //         break;
      //       }
      //     }
          
      //     if (storedChunks.length === 0) {
      //       throw new Error("Could not store any data chunks");
      //     }
          
      //     // Store chunk info
      //     localStorage.setItem(`${OTR_DATA_KEY}_chunk_info`, JSON.stringify({
      //       chunks: storedChunks,
      //       totalRecords: storedChunks.length * MAX_CHUNK_SIZE,
      //       chunkSize: MAX_CHUNK_SIZE
      //     }));
          
      //     // Store metadata with chunking info
      //     metadata.storageStrategy = 'chunked';
      //     metadata.storedChunks = storedChunks.length;
      //     metadata.totalChunks = chunks.length;
      //     metadata.totalRecordsInChunks = storedChunks.length * MAX_CHUNK_SIZE;
      //     localStorage.setItem(OTR_DATA_META_KEY, JSON.stringify(metadata));
          
      //     console.log(`Successfully stored ${storedChunks.length} of ${chunks.length} chunks`);
          
      //     // Still store summary statistics for performance
      //     this.storeSummaryStatistics(data, metadata);
          
      //     return metadata;
      //   } catch (error) {
      //     console.error("Failed to store chunked data, falling back to trip-based storage:", error);
      //     // Continue to trip-based storage as fallback
      //   }
      // }
      
      // // For smaller datasets, use the optimized full storage approach
      // console.log("Using full storage strategy");
      
      // Group data by trip to reduce redundant information
      // First pass: collect all records and identify logical trips by route + start time + contract
      const tempGroups = {};
      
      // Group by DATE_ID_TRIP_ID_CONTRACT_CODE first to handle TRIP_ID uniqueness within dates
      data.forEach(record => {
        if (!record.TRIP_ID || !record.DATE_ID) return;
        
        const uniqueTripId = `${record.DATE_ID}_${record.TRIP_ID}_${record.CONTRACT_CODE || 'UNKNOWN'}`;
        
        if (!tempGroups[uniqueTripId]) {
          tempGroups[uniqueTripId] = {
            tripInfo: {
              TRIP_ID: record.TRIP_ID,
              DATE_ID: record.DATE_ID,
              ROUTE: record.ROUTE,
              ROUTE_VARIANT: record.ROUTE_VARIANT,
              CONTRACT: record.CONTRACT,
              CONTRACT_CODE: record.CONTRACT_CODE,
              DRIVER_ID: record.DRIVER_ID,
              DEPOT: record.DEPOT,
              VEHICLE_SHIFT: record.VEHICLE_SHIFT,
              ROUTE_TYPE_NAME: record.ROUTE_TYPE_NAME,
              DIRECTION: record.DIRECTION,
            },
            stops: [],
            uniqueTripId: uniqueTripId
          };
        }
        
        // Add only necessary stop information to save space
        const compactStopRecord = {
          TSN: record.TSN,
          TSN_FIRST_LAST_IND: record.TSN_FIRST_LAST_IND || 'M',
          IS_TIMING_POINT: record.IS_TIMING_POINT,
          TIME_RECORD_SEQUENCE: record.TIME_RECORD_SEQUENCE
        };
        
        // Store DATE_ID with each stop to maintain date separation
        if (record.DATE_ID !== undefined && record.DATE_ID !== null) {
          compactStopRecord.DATE_ID = record.DATE_ID;
        }
        
        // Use integer values for performance metrics to save space
        if (record.SCHEDULED_ARRIVAL_TIME) {
          const arrivalTime = this.parseTimeToSeconds(record.SCHEDULED_ARRIVAL_TIME);
          if (arrivalTime !== null) {
            // Store exact time without rounding to preserve seconds
            compactStopRecord.S_ARR = arrivalTime;
            // Also store original time string for direct display
            if (typeof record.SCHEDULED_ARRIVAL_TIME === 'string') {
              compactStopRecord.S_ARR_STR = record.SCHEDULED_ARRIVAL_TIME;
            }
          }
        }
        
        if (record.ACTUAL_ARRIVAL_TIME) {
          const arrivalTime = this.parseTimeToSeconds(record.ACTUAL_ARRIVAL_TIME);
          if (arrivalTime !== null) {
            // Store exact time without rounding to preserve seconds
            compactStopRecord.A_ARR = arrivalTime;
            // Also store original time string for direct display
            if (typeof record.ACTUAL_ARRIVAL_TIME === 'string') {
              compactStopRecord.A_ARR_STR = record.ACTUAL_ARRIVAL_TIME;
            }
          }
        }
        
        if (record.SCHEDULED_DEPARTURE_TIME) {
          const departureTime = this.parseTimeToSeconds(record.SCHEDULED_DEPARTURE_TIME);
          if (departureTime !== null) {
            // Store exact time without rounding to preserve seconds
            compactStopRecord.S_DEP = departureTime;
            // Also store original time string for direct display
            if (typeof record.SCHEDULED_DEPARTURE_TIME === 'string') {
              compactStopRecord.S_DEP_STR = record.SCHEDULED_DEPARTURE_TIME;
            }
          }
        }
        
        if (record.ACTUAL_DEPARTURE_TIME) {
          const departureTime = this.parseTimeToSeconds(record.ACTUAL_DEPARTURE_TIME);
          if (departureTime !== null) {
            // Store exact time without rounding to preserve seconds
            compactStopRecord.A_DEP = departureTime;
            // Also store original time string for direct display
            if (typeof record.ACTUAL_DEPARTURE_TIME === 'string') {
              compactStopRecord.A_DEP_STR = record.ACTUAL_DEPARTURE_TIME;
            }
          }
        }
        
        tempGroups[uniqueTripId].stops.push(compactStopRecord);
      });
      
      // Second pass: calculate start times and group by logical trip identity
      const logicalTripGroups = {};
      
      Object.values(tempGroups).forEach(tempTrip => {
        if (tempTrip.stops.length === 0) return;
        
        // Sort stops to find the first stop (earliest scheduled time)
        const sortedStops = tempTrip.stops.sort((a, b) => {
          // Use TIME_RECORD_SEQUENCE if available
          if (a.TIME_RECORD_SEQUENCE && b.TIME_RECORD_SEQUENCE) {
            return Number(a.TIME_RECORD_SEQUENCE) - Number(b.TIME_RECORD_SEQUENCE);
          }
          
          // Fall back to scheduled departure time, then arrival time
          const timeA = a.S_DEP || a.S_ARR || 0;
          const timeB = b.S_DEP || b.S_ARR || 0;
          return timeA - timeB;
        });
        
        // Get start time from first stop
        const firstStop = sortedStops[0];
        const startTimeSeconds = firstStop.S_DEP || firstStop.S_ARR || 0;
        const startTimeHHMM = this.secondsToHHMM(startTimeSeconds);
        
        // Create logical trip identifier: ROUTE_VARIANT_STARTTIME_CONTRACT_CODE
        const routeVariant = tempTrip.tripInfo.ROUTE_VARIANT || tempTrip.tripInfo.ROUTE || 'UNKNOWN';
        const contractCode = tempTrip.tripInfo.CONTRACT_CODE || 'UNKNOWN';
        const logicalTripKey = `${routeVariant}_${startTimeHHMM}_${contractCode}`;
        
        // Group by logical trip identity
        if (!logicalTripGroups[logicalTripKey]) {
          logicalTripGroups[logicalTripKey] = {
            tripInfo: {
              KEY: logicalTripKey,
              START_TIME: startTimeHHMM,
              ROUTE: tempTrip.tripInfo.ROUTE,
              ROUTE_VARIANT: tempTrip.tripInfo.ROUTE_VARIANT,
              CONTRACT: tempTrip.tripInfo.CONTRACT,
              CONTRACT_CODE: tempTrip.tripInfo.CONTRACT_CODE,
              // Use first occurrence for display fields
              TRIP_ID: tempTrip.tripInfo.TRIP_ID,
              DRIVER_ID: tempTrip.tripInfo.DRIVER_ID,
              DEPOT: tempTrip.tripInfo.DEPOT,
              VEHICLE_SHIFT: tempTrip.tripInfo.VEHICLE_SHIFT,
              ROUTE_TYPE_NAME: tempTrip.tripInfo.ROUTE_TYPE_NAME,
              DIRECTION: tempTrip.tripInfo.DIRECTION,
            },
            stops: [],
            dates: new Set()
          };
        }
        
        // Add all stops from this date-specific trip to the logical trip
        logicalTripGroups[logicalTripKey].stops.push(...tempTrip.stops);
        
        // Track dates this logical trip spans
        if (tempTrip.tripInfo.DATE_ID) {
          logicalTripGroups[logicalTripKey].dates.add(tempTrip.tripInfo.DATE_ID);
        }
        
        console.log(`Merged ${tempTrip.uniqueTripId} into logical trip ${logicalTripKey}`);
      });
      
      // Convert to final trip format
      const tripGroups = {};
      Object.entries(logicalTripGroups).forEach(([logicalKey, logicalTrip]) => {
        tripGroups[logicalKey] = {
          tripInfo: logicalTrip.tripInfo,
          stops: logicalTrip.stops,
          logicalTripKey: logicalKey
        };
        
        console.log(`Logical trip ${logicalKey} spans ${logicalTrip.dates.size} dates:`, Array.from(logicalTrip.dates));
      });
      
             // The KEYs are already generated and stored in tripInfo during logical grouping
       console.log(`Generated ${Object.keys(tripGroups).length} logical trips`);
      
      // Convert to array and remove any empty trips
      const trips = Object.values(tripGroups).filter(trip => trip.stops.length > 0);
      
      // Sort trips by date and TRIP_ID for better organization
      trips.sort((a, b) => {
        if (a.tripInfo.DATE_ID !== b.tripInfo.DATE_ID) {
          return a.tripInfo.DATE_ID - b.tripInfo.DATE_ID;
        }
        return a.tripInfo.TRIP_ID - b.tripInfo.TRIP_ID;
      });
      
      console.log(`Organized ${trips.length} trips`);
      
      // Store trips individually to maximize storage
      let storedTripCount = 0;
      try {
        // Try storing trips until we hit quota
        for (let i = 0; i < trips.length; i++) {
          try {
            const tripKey = `${OTR_DATA_KEY}_trip_${trips[i].tripInfo.KEY}`;
            await routesModelExpiryService.db.otrs.put({name: tripKey, trip: trips[i]});
            // localStorage.setItem(tripKey, JSON.stringify(trips[i]));
            storedTripCount++;
          } catch (e) {
            // Stop when we hit storage quota
            console.warn(`Storage quota reached after ${storedTripCount} trips`);
            break;
          }
        }
        
        if (storedTripCount === 0) {
          throw new Error("Could not store any trips due to storage limitations");
        }
        
        // Store trip IDs list for easy retrieval
        const storedTripIds = trips.slice(0, storedTripCount).map(t => t.tripInfo.KEY);
        await routesModelExpiryService.db.otrs.put({name: `${OTR_DATA_KEY}_trip_ids`, tripIds: storedTripIds});
        // localStorage.setItem(`${OTR_DATA_KEY}_trip_ids`, JSON.stringify(storedTripIds));
        
        // Update metadata
        metadata.storageStrategy = 'individual';
        metadata.storedTrips = storedTripCount;
        metadata.totalTrips = trips.length;
        metadata.storedTripIds = storedTripIds;
        await routesModelExpiryService.db.otrs.put({name: OTR_DATA_META_KEY, metadata});
        // localStorage.setItem(OTR_DATA_META_KEY, JSON.stringify(metadata));
        
        console.log(`Successfully stored ${storedTripCount} of ${trips.length} trips`);
        
        return metadata;
      } catch (error) {
        this.clearAllData();
        throw error;
      }
    } catch (error) {
      console.error('Error storing OTR data:', error);
      throw new Error(`Failed to store OTR data: ${error.message}`);
    }
  }
  
  /**
   * Store summary statistics for aggregate analysis
   * @param {Array} data - The full dataset
   * @param {Object} metadata - The metadata object
   */
  async storeSummaryStatistics(data, metadata) {
    try {
      // Calculate and store route-level statistics
      const routeStats = this.calculateRouteStatistics(data);
      await routesModelExpiryService.db.otrs.put({name: `${OTR_DATA_KEY}_route_stats`, routeStats});
      // localStorage.setItem(`${OTR_DATA_KEY}_route_stats`, JSON.stringify(routeStats));
      
      // Calculate and store driver-level statistics
      const driverStats = this.calculateDriverStatistics(data);
      await routesModelExpiryService.db.otrs.put({name: `${OTR_DATA_KEY}_driver_stats`, driverStats});
      // localStorage.setItem(`${OTR_DATA_KEY}_driver_stats`, JSON.stringify(driverStats));
      
      // Store trip summary (without stop details)
      const tripSummary = this.calculateTripSummary(data);
      await routesModelExpiryService.db.otrs.put({name: `${OTR_DATA_KEY}_trip_summary`, tripSummary});
      // localStorage.setItem(`${OTR_DATA_KEY}_trip_summary`, JSON.stringify(tripSummary));
      
      console.log("Stored summary statistics for routes, drivers, and trips");
    } catch (error) {
      console.warn("Failed to store all summary statistics:", error);
    }
  }
  
  /**
   * Calculate route statistics
   * @param {Array} data - The full dataset
   * @returns {Object} Route statistics
   */
  calculateRouteStatistics(data) {
    const routeGroups = {};
    
    // Group data by route
    data.forEach(record => {
      const route = record.ROUTE;
      if (!route) return;
      
      if (!routeGroups[route]) {
        routeGroups[route] = {
          route,
          totalRecords: 0,
          onTimeCount: 0,
          earlyCount: 0,
          lateCount: 0
        };
      }
      
      routeGroups[route].totalRecords++;
      
      // Calculate on-time status using departure variance if available
      const variance = Number.isFinite(record.DEPARTURE_TIME_VARIANCE) ? 
                      record.DEPARTURE_TIME_VARIANCE : 
                      record.ARRIVAL_TIME_VARIANCE;
      
      if (variance !== undefined && variance !== null) {
        if (variance >= -2 && variance <= 2) {
          routeGroups[route].onTimeCount++;
        } else if (variance < -2) {
          routeGroups[route].earlyCount++;
        } else {
          routeGroups[route].lateCount++;
        }
      }
    });
    
    // Calculate percentages
    const routeStats = Object.values(routeGroups).map(group => {
      const totalWithVariance = group.onTimeCount + group.earlyCount + group.lateCount;
      
      return {
        route: group.route,
        totalRecords: group.totalRecords,
        onTimePercentage: totalWithVariance > 0 ? (group.onTimeCount / totalWithVariance) * 100 : 0,
        earlyPercentage: totalWithVariance > 0 ? (group.earlyCount / totalWithVariance) * 100 : 0,
        latePercentage: totalWithVariance > 0 ? (group.lateCount / totalWithVariance) * 100 : 0,
      };
    });
    
    return routeStats;
  }
  
  /**
   * Calculate driver statistics
   * @param {Array} data - The full dataset
   * @returns {Object} Driver statistics
   */
  calculateDriverStatistics(data) {
    const driverGroups = {};
    
    // Group data by driver
    data.forEach(record => {
      const driverId = record.DRIVER_ID;
      if (!driverId) return;
      
      if (!driverGroups[driverId]) {
        driverGroups[driverId] = {
          driverId,
          operatorId: record.OPERATOR_ID,
          depot: record.DEPOT,
          contract: record.CONTRACT,
          stops: [],
          recordCount: 0
        };
      }
      
      driverGroups[driverId].stops.push(record);
      driverGroups[driverId].recordCount++;
    });
    
    // Calculate percentages
    const driverStats = Object.values(driverGroups).map(group => {
      const totalWithVariance = group.onTimeCount + group.earlyCount + group.lateCount;
      
      return {
        driverId: group.driverId,
        totalRecords: group.totalRecords,
        onTimePercentage: totalWithVariance > 0 ? (group.onTimeCount / totalWithVariance) * 100 : 0,
        earlyPercentage: totalWithVariance > 0 ? (group.earlyCount / totalWithVariance) * 100 : 0,
        latePercentage: totalWithVariance > 0 ? (group.lateCount / totalWithVariance) * 100 : 0,
      };
    });
    
    return driverStats;
  }
  
  /**
   * Calculate trip summary
   * @param {Array} data - The full dataset
   * @returns {Array} Trip summary statistics
   */
  calculateTripSummary(data) {
    const tripGroups = {};
    
    // Group data by trip
    data.forEach(record => {
      const tripId = record.TRIP_ID;
      if (!tripId) return;
      
      if (!tripGroups[tripId]) {
        tripGroups[tripId] = {
          tripId,
          route: record.ROUTE,
          date: record.DATE_ID,
          driver: record.DRIVER_ID,
          depot: record.DEPOT,
          firstStops: [],
          midStops: [],
          lastStops: []
        };
      }
      
      // Add to appropriate stop category
      if (record.TSN_FIRST_LAST_IND === 'F') {
        tripGroups[tripId].firstStops.push(record);
      } else if (record.TSN_FIRST_LAST_IND === 'M') {
        tripGroups[tripId].midStops.push(record);
      } else if (record.TSN_FIRST_LAST_IND === 'L') {
        tripGroups[tripId].lastStops.push(record);
      }
    });
    
    // Calculate statistics for each trip
    const tripSummary = Object.values(tripGroups).map(trip => {
      return {
        tripId: trip.tripId,
        route: trip.route,
        date: trip.date,
        driver: trip.driver,
        depot: trip.depot,
        recordCount: trip.firstStops.length + trip.midStops.length + trip.lastStops.length,
        firstStopStats: this.calculateStopTypeStats(trip.firstStops),
        midStopStats: this.calculateStopTypeStats(trip.midStops),
        lastStopStats: this.calculateStopTypeStats(trip.lastStops),
        overallStats: this.calculateStopTypeStats([
          ...trip.firstStops, 
          ...trip.midStops, 
          ...trip.lastStops
        ])
      };
    });
    
    return tripSummary;
  }

  fetchReports = async () => {
    try {
      const reports = await reportsModelData.getAll();
      const reportFilesPromises = Object.values(reports)
      .filter(report => report.fileKey) // Filter out reports without fileKey
      .map(async (report) => {
        console.log(`Processing report: ${report.reportId}, file key: ${report.fileKey}`);
        
        try {
          // Get the file using our simplified helper function
          const url = await getReportUrl(report);
          if (!url) {
            console.error(`Could not access file for report ${report.reportId}`);
            return null;
          }
          
          console.log(`Got URL for file: ${url}`);
          
          // Fetch the file content
          console.log(`Fetching file content...`);
          const response = await fetch(url).catch(e => {
            console.error(`Fetch error:`, e);
            throw e;
          });
          
          const blob = await response.blob();
          // Create a File object from the blob
          const file = new File([blob], report.fileName || `report-${report.reportId}.csv`, { 
            type: report.fileType === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv' 
          });
          
          const result = await this.processFile(file);
          return result;
        } catch (error) {
          console.error(`Error fetching file for report ${report.reportId}:`, error);
          return null;
        }
      });
    
      // Wait for all promises to resolve and filter out null results
      const processedResults = (await Promise.all(reportFilesPromises)).filter(file => !!file);
      
      // Process each file and wait for all processing to complete
      // const processedResults = await Promise.all(
      //   reportFiles.map(async file => {
      //     try {
      //       const result = await this.processFile(file);
      //       return result;
      //     } catch (error) {
      //       console.error(`Error processing file ${file.name}:`, error);
      //       return null;
      //     }
      //   })
      // );

      // Filter out any null results and combine the data
      const combinedData = processedResults
        .filter(result => result && result.data)
        .reduce((acc, curr) => {
          return acc.concat(curr.data);
        }, []);

      // Process combined data to ensure consistent data types (e.g., routes as strings)
      const processedCombinedData = this.processNormalizedData(combinedData);
      
      // Deduplicate the data before returning to ensure uniqueness
      const deduplicatedData = this.deduplicateData(processedCombinedData);
      
      return deduplicatedData;
    } catch (error) {
      console.error("Error fetching reports:", error);
      return [];
    }
  }
  
  /**
   * Retrieve all OTR data from storage
   * @returns {Array} The OTR data records
   */
  async retrieveAllData() {
    try {
      let metadata, allData = [];
      try {
        // Get metadata
        metadata = await routesModelExpiryService.db.otrs.get(OTR_DATA_META_KEY);
      } catch (error) {
        console.error("Error fetching data from DB:", error);
      }

      // If no metadata exists, we need to fetch data
      if (!metadata) {
        console.log("No metadata found, fetching data from API...");
        try {
          // Fetch data from API
          allData = await this.fetchReports();

          // Store the data
          metadata = await this.storeData(allData);
          console.log(`Stored ${allData.length} records from API`);
        } catch (error) {
          console.error("Error fetching data from API:", error);
          return [];
        }
      }
      
      allData = await this.retrieveIndividualTripData(metadata);
      
      // Deduplicate the data to ensure uniqueness
      allData = this.deduplicateData(allData);
      
      // Validate the deduplication worked correctly
      const validationResult = this.validateDataUniqueness(allData);
      if (!validationResult.isValid) {
        console.warn('Data validation failed after deduplication - this should not happen');
      }
      
      // Debug the route information in the data
      if (allData.length > 0) {
        const routesInData = [...new Set(allData.map(record => record.ROUTE))].sort((a, b) => {
          if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
          }
          return String(a).localeCompare(String(b));
        });
        console.log("Routes found in data:", routesInData);
        
        // Check for any potential issues with route data
        const routeTypes = allData.reduce((types, record) => {
          const type = typeof record.ROUTE;
          if (!types[type]) {
            types[type] = 0;
          }
          types[type]++;
          return types;
        }, {});
        console.log("Route data types:", routeTypes);
      }
      
      return allData;
    } catch (error) {
      console.error('Error retrieving OTR data:', error);
      throw new Error(`Failed to retrieve OTR data: ${error.message}`);
    }
  }

  /**
   * Deduplicate OTR data records to ensure uniqueness
   * @param {Array} data - The data records to deduplicate
   * @returns {Array} Deduplicated data records
   */
  deduplicateData(data) {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return data;
    }

    console.log(`Deduplicating ${data.length} records...`);
    
    const uniqueRecords = new Map();
    const duplicates = [];
    
    data.forEach((record, index) => {
      // Create a unique key based on the combination of critical fields
      const uniqueKey = this.createUniqueKey(record);
      
      if (uniqueRecords.has(uniqueKey)) {
        // This is a duplicate
        const existingRecord = uniqueRecords.get(uniqueKey);
        duplicates.push({
          duplicate: record,
          original: existingRecord,
          duplicateIndex: index,
          originalIndex: data.indexOf(existingRecord)
        });
        
        // Keep the record with more complete data (more non-null fields)
        const existingCompleteness = this.calculateRecordCompleteness(existingRecord);
        const newCompleteness = this.calculateRecordCompleteness(record);
        
        if (newCompleteness > existingCompleteness) {
          uniqueRecords.set(uniqueKey, record);
        }
      } else {
        // This is a new unique record
        uniqueRecords.set(uniqueKey, record);
      }
    });
    
    const deduplicatedData = Array.from(uniqueRecords.values());
    
    if (duplicates.length > 0) {
      console.log(`Found ${duplicates.length} duplicate records, removed ${data.length - deduplicatedData.length} duplicates`);
      
      // Log some examples of duplicates for debugging
      const duplicateExamples = duplicates.slice(0, 3);
      duplicateExamples.forEach((dup, index) => {
        console.log(`Duplicate example ${index + 1}:`, {
          uniqueKey: this.createUniqueKey(dup.duplicate),
          duplicate: {
            DATE_ID: dup.duplicate.DATE_ID,
            TRIP_ID: dup.duplicate.TRIP_ID,
            TSN: dup.duplicate.TSN,
            TIME_RECORD_SEQUENCE: dup.duplicate.TIME_RECORD_SEQUENCE,
            _source: dup.duplicate._source
          },
          original: {
            DATE_ID: dup.original.DATE_ID,
            TRIP_ID: dup.original.TRIP_ID,
            TSN: dup.original.TSN,
            TIME_RECORD_SEQUENCE: dup.original.TIME_RECORD_SEQUENCE,
            _source: dup.original._source
          }
        });
      });
    } else {
      console.log("No duplicate records found");
    }
    
    console.log(`Deduplication complete: ${data.length} -> ${deduplicatedData.length} records`);
    return deduplicatedData;
  }

  /**
   * Create a unique key for a record based on critical identifying fields
   * @param {Object} record - The record to create a key for
   * @returns {string} Unique key for the record
   */
  createUniqueKey(record) {
    // Use the combination of fields that should uniquely identify a stop record
    const dateId = record.DATE_ID || '';
    const tripId = record.TRIP_ID || '';
    const vehicleId = record.VEHICLE_ID || '';
    const routeVariant = record.ROUTE_VARIANT || record.ROUTE || '';
    const tsn = record.TSN || '';
    const timeRecordSequence = record.TIME_RECORD_SEQUENCE || '';
    
    // Create a unique key string
    return `${dateId}_${tripId}_${vehicleId}_${routeVariant}_${tsn}_${timeRecordSequence}`;
  }

  /**
   * Calculate the completeness score of a record (number of non-null fields)
   * @param {Object} record - The record to score
   * @returns {number} Completeness score (higher is more complete)
   */
  calculateRecordCompleteness(record) {
    if (!record || typeof record !== 'object') {
      return 0;
    }
    
    let score = 0;
    const fields = [
      'DATE_ID', 'TRIP_ID', 'TSN', 'TIME_RECORD_SEQUENCE',
      'SCHEDULED_ARRIVAL_TIME', 'ACTUAL_ARRIVAL_TIME',
      'SCHEDULED_DEPARTURE_TIME', 'ACTUAL_DEPARTURE_TIME',
      'ARRIVAL_TIME_VARIANCE', 'DEPARTURE_TIME_VARIANCE',
      'ROUTE', 'DRIVER_ID', 'DEPOT', 'OPERATOR_ID', 'CONTRACT'
    ];
    
    fields.forEach(field => {
      if (record[field] !== null && record[field] !== undefined && record[field] !== '') {
        score++;
      }
    });
    
    return score;
  }

  /**
   * Validate data uniqueness and provide detailed reporting
   * @param {Array} data - The data to validate
   * @returns {Object} Validation results with details about duplicates
   */
  validateDataUniqueness(data) {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        isValid: true,
        totalRecords: 0,
        uniqueRecords: 0,
        duplicateCount: 0,
        duplicates: []
      };
    }

    console.log(`Validating uniqueness of ${data.length} records...`);
    
    const uniqueKeys = new Map();
    const duplicates = [];
    const duplicateGroups = new Map();
    
    data.forEach((record, index) => {
      const uniqueKey = this.createUniqueKey(record);
      
      if (uniqueKeys.has(uniqueKey)) {
        // Found a duplicate
        const existingIndex = uniqueKeys.get(uniqueKey);
        const duplicateInfo = {
          uniqueKey,
          duplicateRecord: record,
          originalRecord: data[existingIndex],
          duplicateIndex: index,
          originalIndex: existingIndex,
          duplicateSource: record._source,
          originalSource: data[existingIndex]._source
        };
        
        duplicates.push(duplicateInfo);
        
        // Group duplicates by unique key for analysis
        if (!duplicateGroups.has(uniqueKey)) {
          duplicateGroups.set(uniqueKey, []);
        }
        duplicateGroups.get(uniqueKey).push(duplicateInfo);
      } else {
        uniqueKeys.set(uniqueKey, index);
      }
    });
    
    const validationResult = {
      isValid: duplicates.length === 0,
      totalRecords: data.length,
      uniqueRecords: uniqueKeys.size,
      duplicateCount: duplicates.length,
      duplicates: duplicates,
      duplicateGroups: Array.from(duplicateGroups.entries()).map(([key, group]) => ({
        uniqueKey: key,
        count: group.length + 1, // +1 for the original record
        records: group
      }))
    };
    
    // Log validation results
    if (validationResult.isValid) {
      console.log(`✅ Data validation passed: ${data.length} records are all unique`);
    } else {
      console.log(`❌ Data validation failed: Found ${duplicates.length} duplicates in ${data.length} records`);
      console.log(`   Unique records: ${uniqueKeys.size}, Duplicate records: ${duplicates.length}`);
      
      // Log some example duplicate groups
      const exampleGroups = validationResult.duplicateGroups.slice(0, 3);
      exampleGroups.forEach((group, index) => {
        console.log(`   Duplicate group ${index + 1} (${group.count} records):`, {
          uniqueKey: group.uniqueKey,
          sources: group.records.map(r => r.duplicateSource)
        });
      });
    }
    
    return validationResult;
  }
  
  /**
   * Retrieve individual trip data
   * @returns {Array} Reconstructed data records
   */
  async retrieveIndividualTripData() {
    try {
      const {tripIds} = await routesModelExpiryService.db.otrs.get(`${OTR_DATA_KEY}_trip_ids`);
      console.log(`Found ${tripIds.length} trips in storage`);
      
      let allData = [];
      let tripCount = 0;
      
      for (const tripId of tripIds) {
        try {
          const {trip} = await routesModelExpiryService.db.otrs.get(`${OTR_DATA_KEY}_trip_${tripId}`);
          if (!trip) continue;
          
          // Debug time data availability in compact storage
          if (tripCount === 0) {
            console.log("Sample trip from storage:", trip);
            if (trip.stops && trip.stops.length > 0) {
              const sampleStop = trip.stops[0];
              console.log("Sample stop from storage:", sampleStop);
              console.log("Time fields in compact storage:", {
                S_ARR: sampleStop.S_ARR,
                S_DEP: sampleStop.S_DEP,
                A_ARR: sampleStop.A_ARR,
                A_DEP: sampleStop.A_DEP,
                types: {
                  S_ARR: typeof sampleStop.S_ARR,
                  S_DEP: typeof sampleStop.S_DEP,
                  A_ARR: typeof sampleStop.A_ARR,
                  A_DEP: typeof sampleStop.A_DEP
                }
              });
            }
          }
          tripCount++;
          
          // Check if trip contains individual date information per stop
          const hasStopDates = trip.stops && trip.stops.length > 0 && 
                             trip.stops.some(stop => stop.DATE_ID !== undefined);
          
          // Debug trip date information
          // console.log(`Trip ${tripId} trip.tripInfo.DATE_ID:`, trip.tripInfo.DATE_ID);
          // console.log(`Trip ${tripId} has individual stop dates:`, hasStopDates);
          if (trip.stops && trip.stops.length > 0) {
            const uniqueDates = [...new Set(trip.stops
              .filter(s => s.DATE_ID !== undefined)
              .map(s => s.DATE_ID))];
            if (uniqueDates.length > 0) {
              // console.log(`Trip ${tripId} has ${uniqueDates.length} unique stop dates:`, uniqueDates);
            }
          }
          
          // Reconstruct full records from trip data
          const tripInfo = trip.tripInfo;
          trip.stops.forEach((stop, index) => {
            const fullRecord = {
              ...tripInfo,
              ...stop
            };
            
            // Ensure KEY field is available for all records
            if (tripInfo.KEY) {
              fullRecord.KEY = tripInfo.KEY;
            }
            
            // Make sure each stop has its correct DATE_ID:
            // 1. Use stop's own DATE_ID if available
            // 2. Fall back to tripInfo.DATE_ID if stop doesn't have its own
            if (stop.DATE_ID !== undefined) {
              fullRecord.DATE_ID = stop.DATE_ID;
            } else if (tripInfo.DATE_ID !== undefined) {
              fullRecord.DATE_ID = tripInfo.DATE_ID;
            }
            
            // Add debug identifier for record origin
            fullRecord._source = `trip_${tripId}_stop_${index}`;
            
            // Explicitly copy compact time fields to standard field names for compatibility
            if (stop.S_ARR !== undefined) {
              fullRecord.SCHEDULED_ARRIVAL_TIME = stop.S_ARR;
              // Copy original time string if available
              if (stop.S_ARR_STR) {
                fullRecord.SCHEDULED_ARRIVAL_TIME_STRING = stop.S_ARR_STR;
              }
            }
            if (stop.S_DEP !== undefined) {
              fullRecord.SCHEDULED_DEPARTURE_TIME = stop.S_DEP;
              // Copy original time string if available
              if (stop.S_DEP_STR) {
                fullRecord.SCHEDULED_DEPARTURE_TIME_STRING = stop.S_DEP_STR;
              }
            }
            if (stop.A_ARR !== undefined) {
              fullRecord.ACTUAL_ARRIVAL_TIME = stop.A_ARR;
              // Copy original time string if available
              if (stop.A_ARR_STR) {
                fullRecord.ACTUAL_ARRIVAL_TIME_STRING = stop.A_ARR_STR;
              }
            }
            if (stop.A_DEP !== undefined) {
              fullRecord.ACTUAL_DEPARTURE_TIME = stop.A_DEP;
              // Copy original time string if available
              if (stop.A_DEP_STR) {
                fullRecord.ACTUAL_DEPARTURE_TIME_STRING = stop.A_DEP_STR;
              }
            }
            
            // Also copy the direct string fields for convenience
            if (stop.S_ARR_STR) fullRecord.S_ARR_STR = stop.S_ARR_STR;
            if (stop.S_DEP_STR) fullRecord.S_DEP_STR = stop.S_DEP_STR;
            if (stop.A_ARR_STR) fullRecord.A_ARR_STR = stop.A_ARR_STR;
            if (stop.A_DEP_STR) fullRecord.A_DEP_STR = stop.A_DEP_STR;
            
            // Convert compact time format back to variance
            if ((Number.isFinite(stop.S_DEP) && Number.isFinite(stop.A_DEP)) &&
                !Number.isFinite(fullRecord.DEPARTURE_TIME_VARIANCE)) {
              fullRecord.DEPARTURE_TIME_VARIANCE = stop.A_DEP - stop.S_DEP;
            }
            
            if ((Number.isFinite(stop.S_ARR) && Number.isFinite(stop.A_ARR)) &&
                !Number.isFinite(fullRecord.ARRIVAL_TIME_VARIANCE)) {
              fullRecord.ARRIVAL_TIME_VARIANCE = stop.A_ARR - stop.S_ARR;
            }
            
            // Ensure critical ID fields are preserved exactly as stored
            // Convert numeric TSN values to strings to avoid type errors later
            if (stop.TSN?.length) {
              fullRecord.TSN = String(stop.TSN);
            } else {
              fullRecord.TSN = stop.TSN;
            }
            
            fullRecord.TIME_RECORD_SEQUENCE = stop.TIME_RECORD_SEQUENCE;
            fullRecord.TSN_FIRST_LAST_IND = stop.TSN_FIRST_LAST_IND;
            
            // Add formatted date for convenience
            if (fullRecord.DATE_ID) {
              fullRecord.DATE_FORMATTED = dayjs(String(fullRecord.DATE_ID)).format('YYYY-MM-DD');
            }
            
            allData.push(fullRecord);
          });
        } catch (error) {
          console.warn(`Error retrieving trip ${tripId}:`, error);
        }
      }
      
      // Log unique dates in final data
      const finalDateIds = [...new Set(allData.map(r => r.DATE_ID))];
      console.log(`Final data has ${finalDateIds.length} unique dates:`, finalDateIds);
      
      return allData;
    } catch (error) {
      console.error('Error retrieving individual trip data:', error);
      return [];
    }
  }
  
  /**
   * Analyze trip performance from OTR data
   * @param {Array} data - OTR data records
   * @returns {Array} Trip performance metrics
   */
  async analyzeTripPerformance(data = null) {
    const records = data || await this.retrieveAllData();
    if (!records || !Array.isArray(records) || records.length === 0) {
      console.warn('No data available for trip performance analysis');
      return [];
    }
    
    try {
      // Group records by trip
      const tripGroups = {};
      records.forEach(record => {
        if (!record.TRIP_ID) return;
        
        const tripId = record.TRIP_ID;
        if (!tripGroups[tripId]) {
          tripGroups[tripId] = {
            tripId,
            route: record.ROUTE,
            date: record.DATE_ID,
            driverId: record.DRIVER_ID,
            depot: record.DEPOT,
            operatorId: record.OPERATOR_ID,
            contract: record.CONTRACT,
            stops: [],
            recordCount: 0
          };
        }
        
        tripGroups[tripId].stops.push(record);
        tripGroups[tripId].recordCount++;
      });
      
      // Analyze each trip
      return Object.values(tripGroups).map(trip => {
        // Get first, mid, and last stops
        const firstStops = trip.stops.filter(r => r.TSN_FIRST_LAST_IND === 'F');
        const midStops = trip.stops.filter(r => r.TSN_FIRST_LAST_IND === 'M');
        const lastStops = trip.stops.filter(r => r.TSN_FIRST_LAST_IND === 'L');
        
        // Calculate stop-level stats
        const firstStopStats = this.calculateStopTypeStats(firstStops);
        const midStopStats = this.calculateStopTypeStats(midStops);
        const lastStopStats = this.calculateStopTypeStats(lastStops);
        
        return {
          ...trip,
          firstStopStats,
          midStopStats,
          lastStopStats,
          overallStats: this.calculateStopTypeStats(trip.stops)
        };
      });
    } catch (error) {
      console.error('Error analyzing trip performance:', error);
      return [];
    }
  }
  
  /**
   * Analyze driver performance from OTR data
   * @param {Array} data - OTR data records
   * @returns {Array} Driver performance metrics
   */
  async analyzeDriverPerformance(data = null) {
    const records = data || await this.retrieveAllData();
    if (!records || !Array.isArray(records) || records.length === 0) {
      console.warn('No data available for driver performance analysis');
      return [];
    }
    
    try {
      // Group records by driver
      const driverGroups = {};
      records.forEach(record => {
        if (!record.DRIVER_ID) return;
        
        const driverId = record.DRIVER_ID;
        if (!driverGroups[driverId]) {
          driverGroups[driverId] = {
            driverId,
            operatorId: record.OPERATOR_ID,
            depot: record.DEPOT,
            contract: record.CONTRACT,
            stops: [],
            recordCount: 0
          };
        }
        
        driverGroups[driverId].stops.push(record);
        driverGroups[driverId].recordCount++;
      });
      
      // Analyze each driver
      return Object.values(driverGroups).map(driver => {
        return {
          ...driver,
          overallStats: this.calculateStopTypeStats(driver.stops)
        };
      });
    } catch (error) {
      console.error('Error analyzing driver performance:', error);
      return [];
    }
  }
  
  /**
   * Clear all OTR data from storage
   */
  clearAllData() {
    try {
      // Remove any existing chunks and data
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
       
        if (key && key.startsWith(OTR_DATA_KEY)) {
          keys.push(key);
        }
      }
      
      // Log what we're about to delete to help with debugging
      console.log(`Found ${keys.length} OTR data items to clear from storage:`);
      const keyTypes = keys.reduce((acc, key) => {
        const type = key.includes('_chunk_') ? 'chunk' : 
                    key.includes('_trip_') ? 'trip' : 
                    key.includes('_sample_') ? 'sample' : 'other';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      console.log('OTR data key types:', keyTypes);
      
      // Remove all keys
      keys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Remove metadata and chunk info
      localStorage.removeItem(OTR_DATA_META_KEY);
      localStorage.removeItem(`${OTR_DATA_KEY}_chunk_info`);
      
      console.log(`Cleared ${keys.length} OTR data items from storage`);
    } catch (error) {
      console.error('Error clearing OTR data:', error);
      throw new Error(`Failed to clear OTR data: ${error.message}`);
    }
  }
  
  /**
   * Retrieve OTR data metadata
   * @returns {Object} Metadata about the stored data
   */
  getMetadata() {
    try {
      // const metadataStr = localStorage.getItem(OTR_DATA_META_KEY);
      // return metadataStr ? JSON.parse(metadataStr) : null;
      return routesModelExpiryService.db.otrs.get(OTR_DATA_META_KEY);
    } catch (error) {
      console.error('Error retrieving OTR data metadata:', error);
      return null;
    }
  }
  
  /**
   * Get filtered OTR data based on provided filters
   * @param {Object} filters - The filters to apply
   * @returns {Array} Filtered OTR data
   */
  async getFilteredData(filters = {}) {
    console.log('Filtering OTR data with:', filters);
    const metadata = this.getMetadata();
    if (!metadata) {
      console.warn('No OTR data available to filter');
      return [];
    }
    
    try {
      const unfilteredData = await this.retrieveAllData();
      if (!unfilteredData || unfilteredData.length === 0) {
        console.warn('Retrieved empty dataset');
        return [];
      }
      
      // Apply filters
      let filteredData = [...unfilteredData];
      
      // Filter by date range
      if (filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2) {
        const startDate = filters.dateRange[0];
        const endDate = filters.dateRange[1];
        
        filteredData = filteredData.filter(record => {
          const recordDate = String(record.DATE_ID);
          return recordDate >= startDate.format('YYYYMMDD') && recordDate <= endDate.format('YYYYMMDD');
        });
      }
      
      // Filter by route
      if (filters.routes && Array.isArray(filters.routes) && filters.routes.length > 0) {
        filteredData = filteredData.filter(record => 
          filters.routes.some(route => String(record.ROUTE) === String(route))
        );
      } else if (filters.route) {
        // Backward compatibility for single route filter
        filteredData = filteredData.filter(record => String(record.ROUTE) === String(filters.route));
      }
      
      // Filter by driver
      if (filters.drivers && Array.isArray(filters.drivers) && filters.drivers.length > 0) {
        filteredData = filteredData.filter(record => 
          filters.drivers.some(driver => String(record.DRIVER_ID) === String(driver))
        );
      } else if (filters.driver) {
        // Backward compatibility for single driver filter
        filteredData = filteredData.filter(record => String(record.DRIVER_ID) === String(filters.driver));
      }
      
      // Filter by depot
      if (filters.depots && Array.isArray(filters.depots) && filters.depots.length > 0) {
        filteredData = filteredData.filter(record => 
          filters.depots.some(depot => String(record.DEPOT) === String(depot))
        );
      } else if (filters.depot) {
        // Backward compatibility for single depot filter
        filteredData = filteredData.filter(record => String(record.DEPOT) === String(filters.depot));
      }
      
      // Filter by operator ID
      if (filters.operatorIds && Array.isArray(filters.operatorIds) && filters.operatorIds.length > 0) {
        filteredData = filteredData.filter(record => 
          filters.operatorIds.some(operatorId => String(record.OPERATOR_ID) === String(operatorId))
        );
      } else if (filters.operatorId) {
        // Backward compatibility for single operator filter
        filteredData = filteredData.filter(record => String(record.OPERATOR_ID) === String(filters.operatorId));
      }
      
      // Filter by contract
      if (filters.contracts && Array.isArray(filters.contracts) && filters.contracts.length > 0) {
        filteredData = filteredData.filter(record => 
          filters.contracts.some(contract => String(record.CONTRACT) === String(contract))
        );
      } else if (filters.contract) {
        // Backward compatibility for single contract filter
        filteredData = filteredData.filter(record => String(record.CONTRACT) === String(filters.contract));
      }
      
      // Filter by trip ID
      if (filters.tripIds && Array.isArray(filters.tripIds) && filters.tripIds.length > 0) {
        filteredData = filteredData.filter(record => {
          const tripKey = record.KEY || record.TRIP_ID;
          return filters.tripIds.some(tripId => String(tripKey) === String(tripId));
        });
      } else if (filters.tripId) {
        // Backward compatibility for single trip filter
        filteredData = filteredData.filter(record => {
          const tripKey = record.KEY || record.TRIP_ID;
          return String(tripKey) === String(filters.tripId);
        });
      }
      
      // Filter by stop type
      if (filters.stopType) {
        filteredData = filteredData.filter(record => String(record.STOP_TYPE) === String(filters.stopType));
      }
      
      // Filter by time period
      if (filters.timePeriod && filters.timePeriod !== 'all') {
        filteredData = filteredData.filter(record => {
          const timePeriod = this.getTimePeriod(record.SCHEDULED_ARRIVAL_TIME);
          return timePeriod === filters.timePeriod;
        });
      }
      
      console.log(`Filtered data from ${unfilteredData.length} to ${filteredData.length} records`);
      return filteredData;
    } catch (error) {
      console.error('Error filtering OTR data:', error);
      return [];
    }
  }
  
  /**
   * Generate summary statistics for OTR data
   * @param {Array} data - The OTR data to analyze (optional, will use all data if not provided)
   * @returns {Object} Summary statistics
   */
  async generateSummaryStats(data = null) {
    const records = data || await this.retrieveAllData();
    
    if (!records || records.length === 0) {
      return {
        totalTrips: 0,
        totalStops: 0,
        onTimePercentage: 0,
        earlyPercentage: 0,
        latePercentage: 0
      };
    }
    
    // Count unique trips
    const uniqueTrips = new Set(records.map(r => r.TRIP_ID)).size;
    
    // Count records with arrival variance
    const recordsWithVariance = records.filter(r => r.ARRIVAL_TIME_VARIANCE !== null && r.ARRIVAL_TIME_VARIANCE !== undefined);
    
    // On-time records (within 2 minutes of scheduled time)
    const onTimeRecords = recordsWithVariance.filter(r => r.ARRIVAL_TIME_VARIANCE >= -2 && r.ARRIVAL_TIME_VARIANCE <= 5);
    
    // Early records (more than 2 minutes early)
    const earlyRecords = recordsWithVariance.filter(r => r.ARRIVAL_TIME_VARIANCE < -2);
    
    // Late records (more than 5 minutes late)
    const lateRecords = recordsWithVariance.filter(r => r.ARRIVAL_TIME_VARIANCE > 5);
    
    // Calculate percentages
    const totalWithVariance = recordsWithVariance.length;
    const onTimePercentage = totalWithVariance > 0 ? (onTimeRecords.length / totalWithVariance) * 100 : 0;
    const earlyPercentage = totalWithVariance > 0 ? (earlyRecords.length / totalWithVariance) * 100 : 0;
    const latePercentage = totalWithVariance > 0 ? (lateRecords.length / totalWithVariance) * 100 : 0;
    
    return {
      totalTrips: uniqueTrips,
      totalStops: records.length,
      onTimePercentage,
      earlyPercentage,
      latePercentage,
      onTimeCount: onTimeRecords.length,
      earlyCount: earlyRecords.length,
      lateCount: lateRecords.length
    };
  }
  
  /**
   * Calculate statistics for a set of stop records
   * @param {Array} stopRecords - The stop records to analyze
   * @returns {Object} Statistics for the stops
   */
  calculateStopTypeStats(stopRecords) {
    // Check if we have data
    if (!stopRecords || !Array.isArray(stopRecords) || stopRecords.length === 0) {
      return {
        count: 0,
        onTimePercentage: 0,
        earlyPercentage: 0,
        latePercentage: 0,
        averageVariance: 0
      };
    }
    
    // Filter to records that have variance data
    const recordsWithVariance = stopRecords.filter(r => 
      (Number.isFinite(r.DEPARTURE_TIME_VARIANCE) ||
      (Number.isFinite(r.ARRIVAL_TIME_VARIANCE)))
    );
    
    if (recordsWithVariance.length === 0) {
      return {
        count: stopRecords.length,
        onTimePercentage: 0,
        earlyPercentage: 0,
        latePercentage: 0,
        averageVariance: 0
      };
    }
    
    // On-time records (within threshold values)
    const onTimeRecords = recordsWithVariance.filter(r => {
      const variance = Number.isFinite(r.DEPARTURE_TIME_VARIANCE)
        ? r.DEPARTURE_TIME_VARIANCE  // Use departure variance if available
        : r.ARRIVAL_TIME_VARIANCE;   // Fall back to arrival variance
      
      // Use OTR_THRESHOLDS constants
      return variance > this.thresholds.early && variance < this.thresholds.late;
    });
    
    // Early records (more than threshold minutes early)
    const earlyRecords = recordsWithVariance.filter(r => {
      const variance = Number.isFinite(r.DEPARTURE_TIME_VARIANCE)
        ? r.DEPARTURE_TIME_VARIANCE  // Use departure variance if available
        : r.ARRIVAL_TIME_VARIANCE;   // Fall back to arrival variance
      
      return variance <= this.thresholds.early;
    });
    
    // Late records (more than threshold minutes late)
    const lateRecords = recordsWithVariance.filter(r => {
      const variance = Number.isFinite(r.DEPARTURE_TIME_VARIANCE)
        ? r.DEPARTURE_TIME_VARIANCE  // Use departure variance if available
        : r.ARRIVAL_TIME_VARIANCE;   // Fall back to arrival variance
      
      return variance >= this.thresholds.late;
    });
    
    // Calculate average variance
    const totalVariance = recordsWithVariance.reduce((sum, r) => {
      const variance = Number.isFinite(r.DEPARTURE_TIME_VARIANCE)
        ? r.DEPARTURE_TIME_VARIANCE  // Use departure variance if available
        : r.ARRIVAL_TIME_VARIANCE;   // Fall back to arrival variance
      
      return sum + variance;
    }, 0);
    const averageVariance = totalVariance / recordsWithVariance.length;
    
    // Calculate percentages
    const totalWithVariance = recordsWithVariance.length;
    const onTimePercentage = (onTimeRecords.length / totalWithVariance) * 100;
    const earlyPercentage = (earlyRecords.length / totalWithVariance) * 100;
    const latePercentage = (lateRecords.length / totalWithVariance) * 100;
    
    return {
      count: stopRecords.length,
      onTimePercentage,
      earlyPercentage,
      latePercentage,
      averageVariance,
      onTimeCount: onTimeRecords.length,
      earlyCount: earlyRecords.length,
      lateCount: lateRecords.length
    };
  }
  
  /**
   * Parse time string to minutes past midnight
   * @param {string} timeString - The time string to parse
   * @returns {number|null} Minutes past midnight or null if parsing fails
   */
  parseTimeToSeconds(timeString) {
    if (!timeString) return null;
    
    try {
      // Extract the time part if it includes a date
      let timePart = timeString;
      if (timeString.includes(' ')) {
        timePart = timeString.split(' ')[1];
      }
      
      // Parse HH:MM:SS format
      if (timePart.includes(':')) {
        const parts = timePart.split(':');
        let hours = 0, minutes = 0, seconds = 0;
        
        if (parts.length >= 3) {
          hours = parseInt(parts[0], 10);
          minutes = parseInt(parts[1], 10);
          seconds = parseInt(parts[2], 10);
        } else if (parts.length === 2) {
          hours = parseInt(parts[0], 10);
          minutes = parseInt(parts[1], 10);
        }
        
        if (isNaN(hours) || isNaN(minutes)) {
          return null;
        }
        
        // Convert to seconds past midnight - ensure seconds precision is preserved
        return hours * 3600 + minutes * 60 + seconds;
      }
      
      // Try to parse as Date object
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
      }
      
      return null;
    } catch (error) {
      console.warn(`Error parsing time string: ${timeString}`, error);
      return null;
    }
  }

  /**
   * Convert seconds since midnight to HH:mm format
   * @param {number} seconds - Seconds since midnight
   * @returns {string} Time in HH:mm format
   */
  secondsToHHMM(seconds) {
    if (!seconds || seconds < 0) return '00:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  /**
   * Determine time period from a time string
   * @param {string} timeString - The time string to analyze
   * @returns {string} The time period code (e.g., 'MORNING')
   */
  getTimePeriod(timeString) {
    if (!timeString) return 'unknown';
    
    try {
      // Extract time part from datetime string
      let timePart = timeString;
      if (timeString.includes(' ')) {
        timePart = timeString.split(' ')[1];
      }
      
      // Default to empty string if splitting fails
      if (!timePart) return 'unknown';
      
      // Extract hours:minutes
      const timeForComparison = timePart.substring(0, 5);
      
      const TIME_PERIODS = {
        MORNING: { start: '06:00', end: '10:00' },
        MIDDAY: { start: '10:01', end: '15:00' },
        AFTERNOON: { start: '15:01', end: '19:00' },
        EVENING: { start: '19:01', end: '23:59' },
        NIGHT: { start: '00:00', end: '05:59' }
      };
      
      for (const [period, { start, end }] of Object.entries(TIME_PERIODS)) {
        if (timeForComparison >= start && timeForComparison <= end) {
          return period;
        }
      }
      
      return 'unknown';
    } catch (error) {
      console.warn('Error determining time period:', error);
      return 'unknown';
    }
  }
  
  /**
   * Load and cache stop data from the Busable API
   * @returns {Promise<Object>} Mapping of TSN to coordinates
   */
  async loadStopCoordinates() {
    try {
      // Check if we have cached stops
      const cachedStopsStr = localStorage.getItem(OTR_STOP_DATA_KEY);
      if (cachedStopsStr) {
        const cachedStops = JSON.parse(cachedStopsStr);
        if (cachedStops.timestamp && Date.now() - cachedStops.timestamp < 24 * 60 * 60 * 1000) {
          // Use cached stops if they're less than 24 hours old
          console.log("Using cached stop data");
          return cachedStops.stops;
        }
      }
      
      // Fetch stops from API
      console.log("Fetching stops from API");
      const stops = await getStops();
      
      if (!stops || !Array.isArray(stops)) {
        console.warn("Invalid stop data returned from API");
        return {};
      }
      
      console.log(`Loaded ${stops.length} stops from Busable API`);
      
      // Build a mapping of stop codes (TSN) to coordinates
      const stopMap = {};
      const tsnsFound = new Set();
      
      stops.forEach(stop => {
        if (stop.stopCode) {
          // Store by stopCode which should match TSN
          stopMap[stop.stopCode] = {
            latitude: stop.lat,
            longitude: stop.lon,
            stopName: stop.stopName
          };
          tsnsFound.add(stop.stopCode);
          
          // Also store by numeric ID without leading zeros
          const numericCode = String(parseInt(stop.stopCode, 10));
          if (numericCode !== stop.stopCode) {
            stopMap[numericCode] = {
              latitude: stop.lat,
              longitude: stop.lon,
              stopName: stop.stopName
            };
            tsnsFound.add(numericCode);
          }
        }
      });
      
      // Try to fetch master stops if we're missing specific stop IDs
      // This is a fallback to check broader master data
      try {
        // Create a worldwide bounds to get all master stops
        const worldBounds = {
          sw: { lat: -90, lon: -180 },
          ne: { lat: 90, lon: 180 }
        };
        
        console.log("Fetching additional stops from master data");
        const masterStops = await fetchMasterStopsInBounds({
          bounds: worldBounds,
          excluding: [],
          types: ['bus']
        });
        
        if (masterStops && Array.isArray(masterStops) && masterStops.length > 0) {
          console.log(`Found ${masterStops.length} stops in master data`);
          
          masterStops.forEach(stop => {
            if (stop.stopCode) {
              // Only add if we don't already have this stop
              if (!stopMap[stop.stopCode]) {
                stopMap[stop.stopCode] = {
                  latitude: stop.lat,
                  longitude: stop.lon,
                  stopName: stop.stopName || `Stop ${stop.stopCode}`
                };
                tsnsFound.add(stop.stopCode);
              }
              
              // Also store by numeric ID without leading zeros
              const numericCode = String(parseInt(stop.stopCode, 10));
              if (numericCode !== stop.stopCode && !stopMap[numericCode]) {
                stopMap[numericCode] = {
                  latitude: stop.lat,
                  longitude: stop.lon,
                  stopName: stop.stopName || `Stop ${stop.stopCode}`
                };
                tsnsFound.add(numericCode);
              }
            }
          });
        }
      } catch (masterError) {
        console.warn("Error fetching master stops:", masterError);
      }
      
      // Check for specific TSN
      if (tsnsFound.has('257144')) {
        console.log("TSN 257144 found in master data!");
      } else {
        console.warn("TSN 257144 NOT found in master data");
        
        // Look for similar TSNs
        const similar = Array.from(tsnsFound).filter(tsn => 
          tsn.includes('257144') || tsn.includes('25714') || 
          tsn.startsWith('257') || tsn.endsWith('7144')
        );
        
        if (similar.length > 0) {
          console.log("Similar TSNs found:", similar);
        }
      }
      
      console.log(`Created mapping for ${Object.keys(stopMap).length} unique TSNs`);
      
      // Cache the results
      localStorage.setItem(OTR_STOP_DATA_KEY, JSON.stringify({
        timestamp: Date.now(),
        stops: stopMap
      }));
      
      return stopMap;
    } catch (error) {
      console.error("Error loading stop coordinates:", error);
      return {};
    }
  }
  
  /**
   * Enhance OTR data with stop coordinates
   * @param {Array} data - The OTR data records
   * @returns {Promise<Array>} Enhanced data with coordinates
   */
  async enhanceWithStopCoordinates(data) {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return data;
    }
    
    // Load stop coordinates
    const stopCoordinates = await this.loadStopCoordinates();
    
    // Count matches and misses for debugging
    let matchCount = 0;
    let missCount = 0;
    const missingTSNs = new Set();
    
    // Enhance data with coordinates
    const enhancedData = data.map(record => {
      const tsn = record.TSN;
      const enhancedRecord = { ...record };
      
      if (tsn && stopCoordinates[tsn]) {
        // Add coordinates from stop data
        enhancedRecord.latitude = stopCoordinates[tsn].latitude;
        enhancedRecord.longitude = stopCoordinates[tsn].longitude;
        enhancedRecord.stopId = tsn;
        enhancedRecord.stopName = stopCoordinates[tsn].stopName;
        matchCount++;
      } else {
        // Try matching with a version without leading zeros
        const numericTsn = tsn ? String(parseInt(tsn, 10)) : null;
        if (numericTsn && stopCoordinates[numericTsn]) {
          enhancedRecord.latitude = stopCoordinates[numericTsn].latitude;
          enhancedRecord.longitude = stopCoordinates[numericTsn].longitude;
          enhancedRecord.stopId = numericTsn;
          enhancedRecord.stopName = stopCoordinates[numericTsn].stopName;
          matchCount++;
        } else {
          missCount++;
          if (tsn) missingTSNs.add(tsn);
        }
      }
      
      return enhancedRecord;
    });
    
    console.log(`Stop coordinate matching: ${matchCount} matches, ${missCount} misses`);
    if (missingTSNs.size > 0) {
      console.log(`Missing TSNs (first 10): ${Array.from(missingTSNs).slice(0, 10).join(', ')}${missingTSNs.size > 10 ? '...' : ''}`);
      if (missingTSNs.has('257144')) {
        console.warn("TSN 257144 is in the missing list!");
      }
    }
    
    return enhancedData;
  }
  
  /**
   * Utility method to check for a specific TSN in the master stops data
   * @param {string} tsn - The TSN ID to check
   * @returns {Promise<boolean>} Whether the TSN exists in master data
   */
  async checkTSNExists(tsn) {
    if (!tsn) return false;
    
    try {
      console.log(`Checking if TSN ${tsn} exists in master data...`);
      
      // First check localStorage cache
      const cachedStopsStr = localStorage.getItem(OTR_STOP_DATA_KEY);
      if (cachedStopsStr) {
        const cachedStops = JSON.parse(cachedStopsStr);
        const stopMap = cachedStops.stops || {};
        
        // Check direct match
        if (stopMap[tsn]) {
          console.log(`TSN ${tsn} found in cached data!`);
          console.log('Associated stop data:', stopMap[tsn]);
          return true;
        }
        
        // Check numeric match (without leading zeros)
        const numericTsn = String(parseInt(tsn, 10));
        if (numericTsn !== tsn && stopMap[numericTsn]) {
          console.log(`TSN ${tsn} (as ${numericTsn}) found in cached data!`);
          console.log('Associated stop data:', stopMap[numericTsn]);
          return true;
        }
        
        console.log(`TSN ${tsn} not found in cached data, fetching fresh data...`);
      }
      
      // Fetch fresh stops data
      const stops = await getStops();
      
      if (!stops || !Array.isArray(stops)) {
        console.warn("Invalid stop data returned from API");
        return false;
      }
      
      console.log(`Received ${stops.length} stops from API`);
      
      // Look for direct match on stopCode
      const directMatch = stops.find(stop => stop.stopCode === tsn);
      if (directMatch) {
        console.log(`Found direct match for TSN ${tsn}:`, directMatch);
        return true;
      }
      
      // Look for match on numeric version
      const numericTsn = String(parseInt(tsn, 10));
      const numericMatch = stops.find(stop => stop.stopCode === numericTsn);
      if (numericMatch) {
        console.log(`Found numeric match for TSN ${tsn} (as ${numericTsn}):`, numericMatch);
        return true;
      }
      
      // Look for similar TSNs to help troubleshooting
      const similarMatches = stops.filter(stop => {
        if (!stop.stopCode) return false;
        return stop.stopCode.includes(tsn.substring(0, 4)) || 
               stop.stopCode.includes(tsn.substring(tsn.length - 4)) ||
               stop.stopCode.startsWith(tsn.substring(0, 3)) ||
               stop.stopCode.endsWith(tsn.substring(tsn.length - 3));
      });
      
      if (similarMatches.length > 0) {
        console.log(`Found ${similarMatches.length} similar matches for TSN ${tsn}:`, 
          similarMatches.map(s => ({ stopCode: s.stopCode, stopName: s.stopName }))
        );
      }
      
      console.log(`TSN ${tsn} not found in master data`);
      return false;
    } catch (error) {
      console.error(`Error checking TSN ${tsn}:`, error);
      return false;
    }
  }
  
  /**
   * Get filtered data with stop coordinates
   * @param {Object} filters - The filters to apply
   * @returns {Promise<Array>} Filtered and enhanced data
   */
  async getFilteredDataWithCoordinates(filters = {}) {
    const filteredData = await this.getFilteredData(filters);
    return await this.enhanceWithStopCoordinates(filteredData);
  }
  
  /**
   * Get the date range that contains data
   * @returns {Object|null} Object with startDate and endDate, or null if no data
   */
  async getDataDateRange() {
    const allData = await this.retrieveAllData();
    
    if (!allData || allData.length === 0) {
      return null;
    }
    
    // Find all date fields we might be working with
    const dateFields = ['DATE_ID', 'DATE_FORMATTED', 'TRIP_DATE', 'DATE'];
    
    // Extract all dates from the data
    const dates = [];
    allData.forEach(record => {
      let foundDate = null;
      
      // Try each date field
      for (const field of dateFields) {
        if (record[field]) {
          let dateStr = record[field];
          
          // Convert YYYYMMDD format to a date
          if (typeof dateStr === 'string' && dateStr.length === 8 && /^\d+$/.test(dateStr)) {
            const year = dateStr.substring(0, 4);
            const month = dateStr.substring(4, 6);
            const day = dateStr.substring(6, 8);
            dateStr = `${year}-${month}-${day}`;
          }
          
          try {
            const date = dayjs(dateStr);
            if (date.isValid()) {
              dates.push(date);
              foundDate = true;
              break;
            }
          } catch (e) {
            // Invalid date, try next field
          }
        }
      }
    });
    
    if (dates.length === 0) {
      return null;
    }
    
    // Sort dates and find min/max
    dates.sort((a, b) => a - b);
    
    return {
      startDate: dates[0].format('YYYY-MM-DD'),
      endDate: dates[dates.length - 1].format('YYYY-MM-DD')
    };
  }
};

// Export singleton instance
export default new OTRDataService(); 